import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X, MapPin, PartyPopper, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const KEY = "masaya_lead_popup_v1";
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const HIDE_ON = ["/book", "/quote", "/portal", "/login", "/register", "/admin", "/staff", "/payment", "/pay-", "/reset-", "/forgot-", "/unsubscribe"];

const SERVICES = [["airbnb", "Airbnb Turnover Cleaning"], ["residential", "Residential Cleaning"], ["deep", "Deep Cleaning"]];
const TIMEFRAMES = ["As soon as possible", "Within 7 days", "Within 30 days", "Just researching"];

export default function LeadPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", zip: "", service: "", timeframe: "", message: "", marketing_consent: false });

  const hidden = HIDE_ON.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (hidden) return;
    let last = 0;
    try { last = Number(localStorage.getItem(KEY) || 0); } catch (e) { /* ignore */ }
    if (last && Date.now() - last < THIRTY_DAYS) return;
    const t = setTimeout(() => setOpen(true), 8000);
    return () => clearTimeout(t);
  }, [hidden, pathname]);

  const remember = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* ignore */ } };
  const close = () => { remember(); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.zip || !form.service || !form.timeframe) {
      toast.error("Please fill in the required fields."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/inquiries", form);
      remember();
      setResult(data);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  if (hidden || !open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="lead-popup" role="dialog" aria-modal="true">
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <button onClick={close} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-[#F5F0E8] flex items-center justify-center text-[#635B52] hover:bg-[#E6DEC3]" data-testid="lead-popup-close"><X size={18} /></button>

        {!result ? (
          <form onSubmit={submit} className="p-6 sm:p-7">
            <div className="flex items-center gap-2 text-[#B87333]"><MapPin size={18} /><span className="text-xs font-semibold uppercase tracking-wider">Austin · Pflugerville · Round Rock</span></div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-[#2C2621]">Need Cleaning in the Austin Area?</h2>
            <p className="mt-1.5 text-sm text-[#635B52]">Check local availability and enjoy 10% off your first eligible cleaning.</p>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name*" className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full" data-testid="lead-first-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email*" className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full" data-testid="lead-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full" data-testid="lead-phone" />
                <input required value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="ZIP code*" inputMode="numeric" className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full" data-testid="lead-zip" />
              </div>
              <select required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full text-[#2C2621]" data-testid="lead-service">
                <option value="">Service needed*</option>
                {SERVICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select required value={form.timeframe} onChange={(e) => setForm({ ...form, timeframe: e.target.value })} className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full text-[#2C2621]" data-testid="lead-timeframe">
                <option value="">Preferred timeframe*</option>
                {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything else? (optional)" className="rounded-xl border border-[#E6DEC3] px-3 py-2.5 text-sm w-full" data-testid="lead-message" />
              <label className="flex items-start gap-2 text-xs text-[#635B52] cursor-pointer">
                <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5 accent-[#B87333]" data-testid="lead-consent" />
                I agree to receive occasional promotions from Masaya Hospitality Cleaning Services. I can unsubscribe at any time.
              </label>
            </div>

            <button type="submit" disabled={busy} className="mt-4 w-full bg-[#B87333] text-white rounded-full py-3 font-semibold text-sm disabled:opacity-60 hover:bg-[#a5652c] transition-colors" data-testid="lead-submit">
              {busy ? "Checking…" : "CHECK AVAILABILITY & GET 10% OFF"}
            </button>
          </form>
        ) : (
          <div className="p-7 text-center" data-testid="lead-result">
            {result.in_area ? (
              <>
                <PartyPopper className="mx-auto text-[#B87333]" size={40} />
                <h3 className="mt-3 font-serif text-2xl text-[#2C2621]">Great news—we serve your area!</h3>
                {result.promo?.active ? (
                  <>
                    <p className="mt-2 text-sm text-[#635B52]">Use code <strong className="text-[#B87333]">{result.promo.code}</strong> to receive {result.promo.percent}% off your first eligible cleaning of ${Number(result.promo.min).toFixed(0)} or more. Limited-time offer.</p>
                    <div className="mt-3 inline-flex items-center gap-2 bg-[#F5F0E8] rounded-xl px-4 py-2 text-lg font-bold tracking-wider text-[#B87333]" data-testid="lead-promo-code"><Sparkles size={18} /> {result.promo.code}</div>
                  </>
                ) : (
                  <p className="mt-2 text-sm text-[#635B52]">We'd love to get your place sparkling. Book online to check live availability.</p>
                )}
                <Link to="/book" onClick={close} className="mt-5 block w-full bg-[#B87333] text-white rounded-full py-3 font-semibold text-sm hover:bg-[#a5652c] transition-colors" data-testid="lead-book-now">BOOK NOW</Link>
              </>
            ) : (
              <>
                <MapPin className="mx-auto text-[#635B52]" size={38} />
                <h3 className="mt-3 font-serif text-2xl text-[#2C2621]">Thank you!</h3>
                <p className="mt-2 text-sm text-[#635B52]">We may not serve your area yet, but we've saved your inquiry and will contact you if service becomes available.</p>
                <button onClick={close} className="mt-5 w-full border border-[#E6DEC3] text-[#2C2621] rounded-full py-3 font-medium text-sm" data-testid="lead-close-thanks">Close</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
